import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FileAttachments from '../components/FileAttachments';
import { RotateCcw, Plus, XCircle, CheckCircle2, ChevronDown, ChevronUp, Paperclip, Send, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { BadgeEstadoFirma, ModalPasarPdteFirma, ModalRevertirBorrador, esEstadoFirmado, IconoBloqueado } from '../components/FlujoFirmaAcciones';
import { ModalEliminarRegistro, esAdmin } from '../components/RegistroAcciones';

// ═══════════════════════════════════════════════════════════════
// MODAL: Crear nueva devolución
// ═══════════════════════════════════════════════════════════════
function ModalDevolucion({ onClose, onGuardado }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ fecha: today, codigo_articulo: '', articulo: '', cantidad: '', lote: '', caducidad: '', motivo: '', proveedor: '', cliente: '', recibido_nombre: '', observaciones: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.articulo || !form.cantidad || !form.motivo) { setError('Artículo, cantidad y motivo son obligatorios'); return; }
    setLoading(true);
    try { const r = await api.post('/registros/devoluciones', form); setSavedId(r.data.id); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RotateCcw size={18} color="var(--color-primary)" />Nueva Devolución de Producto Sanitario</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && <div className="alert alert-success"><CheckCircle2 size={16} /> Devolución guardada en estado Borrador. Puedes adjuntar documentación.</div>}
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha *</label><input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Código artículo</label><input className="form-control" value={form.codigo_articulo} onChange={e => set('codigo_articulo', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Artículo / Descripción *</label><input className="form-control" value={form.articulo} onChange={e => set('articulo', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">Cantidad *</label><input type="number" className="form-control" value={form.cantidad} onChange={e => set('cantidad', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Lote</label><input className="form-control" value={form.lote} onChange={e => set('lote', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Caducidad</label><input type="date" className="form-control" value={form.caducidad} onChange={e => set('caducidad', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Motivo de devolución *</label><textarea className="form-control" rows={3} value={form.motivo} onChange={e => set('motivo', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Proveedor / Fabricante</label><input className="form-control" value={form.proveedor} onChange={e => set('proveedor', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Cliente</label><input className="form-control" value={form.cliente} onChange={e => set('cliente', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Recibido por</label><input className="form-control" value={form.recibido_nombre} onChange={e => set('recibido_nombre', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-group"><label className="form-label">Observaciones</label><textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} disabled={!!savedId} /></div>
          <FileAttachments seccion="devoluciones" entidadTipo="devolucion" entidadId={savedId} label="Adjuntar albarán / documentación de devolución" />
        </div>
        <div className="modal-footer">
          {!savedId ? (
            <><button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar} disabled={loading}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />} Guardar
              </button></>
          ) : (
            <><button className="btn btn-outline" onClick={onClose}>Cerrar sin más archivos</button>
              <button className="btn btn-primary" onClick={onGuardado}><CheckCircle2 size={16} /> Finalizar</button></>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Editar devolución (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEditarDevolucion({ registro, onClose, onActualizado }) {
  const [form, setForm] = useState({
    fecha: registro.fecha, codigo_articulo: registro.codigo_articulo || '',
    articulo: registro.articulo || '', cantidad: registro.cantidad || '',
    lote: registro.lote || '', caducidad: registro.caducidad || '',
    motivo: registro.motivo || '', proveedor: registro.proveedor || '',
    cliente: registro.cliente || '', recibido_nombre: registro.recibido_nombre || '',
    observaciones: registro.observaciones || ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/registros/devoluciones/${registro.id}`, { ...form, password });
      onActualizado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={20} color="#3b82f6" /> Editar Devolución
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> <strong>Acción restringida:</strong> Solo administradores. Se requiere contraseña.
          </div>

          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha</label>
              <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Código artículo</label>
              <input className="form-control" value={form.codigo_articulo} onChange={e => set('codigo_articulo', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Artículo</label>
            <input className="form-control" value={form.articulo} onChange={e => set('articulo', e.target.value)} /></div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">Cantidad</label>
              <input type="number" className="form-control" value={form.cantidad} onChange={e => set('cantidad', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Lote</label>
              <input className="form-control" value={form.lote} onChange={e => set('lote', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Caducidad</label>
              <input type="date" className="form-control" value={form.caducidad} onChange={e => set('caducidad', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Motivo</label>
            <textarea className="form-control" rows={3} value={form.motivo} onChange={e => set('motivo', e.target.value)} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Proveedor</label>
              <input className="form-control" value={form.proveedor} onChange={e => set('proveedor', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Cliente</label>
              <input className="form-control" value={form.cliente} onChange={e => set('cliente', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Recibido por</label>
            <input className="form-control" value={form.recibido_nombre} onChange={e => set('recibido_nombre', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} /></div>

          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILA con todos los botones de acción
// ═══════════════════════════════════════════════════════════════
function FilaDevolucion({ r, isAdmin, onPasarPdte, onRevertir, onEditar, onEliminar }) {
  const [expanded, setExpanded] = useState(false);
  const estado = r.estado || 'borrador';
  const bloqueado = esEstadoFirmado(estado, false);

  return (
    <>
      <tr>
        <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 700 }}>{r.numero_devolucion}</span></td>
        <td>{new Date(r.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ fontWeight: 500 }}>{r.articulo}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{r.cantidad}</td>
        <td style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)' }}>{r.lote || '—'}</td>
        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-2)', maxWidth: 200 }}>{r.motivo}</td>
        <td><BadgeEstadoFirma estado={estado} /></td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="devoluciones" entidadTipo="devolucion" entidadId={r.id} compact />
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            
            {/* Pasar a Pdte. firma — solo si está en borrador */}
            {estado === 'borrador' && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onPasarPdte(r)} 
                title="Pasar a Pdte. firma" style={{ color: '#f59e0b' }}>
                <Send size={13} />
              </button>
            )}
            
            {/* Revertir a borrador — solo admin si está en pendiente_firma */}
            {estado === 'pendiente_firma' && isAdmin && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onRevertir(r)} 
                title="Revertir a Borrador (admin)" style={{ color: '#6b7280' }}>
                <RotateCcw size={13} />
              </button>
            )}
            
            {/* Editar — admin, bloqueado si firmado */}
            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEditar(r)} 
                title="Editar (admin)" style={{ color: '#3b82f6' }}>
                <Pencil size={13} />
              </button>
            )}
            
            {/* Eliminar — admin, bloqueado si firmado */}
            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(r)} 
                title="Eliminar (admin)" style={{ color: 'var(--color-danger)' }}>
                <Trash2 size={13} />
              </button>
            )}
            
            {/* Icono bloqueado si está firmado */}
            {bloqueado && <IconoBloqueado estado={estado} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={8} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
          <FileAttachments seccion="devoluciones" entidadTipo="devolucion" entidadId={r.id} label="Subir albarán o documentación" />
        </td></tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA principal
// ═══════════════════════════════════════════════════════════════
export default function DevolucionesPage() {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editarRegistro, setEditarRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [pasarPdteRegistro, setPasarPdteRegistro] = useState(null);
  const [revertirRegistro, setRevertirRegistro] = useState(null);
  const [success, setSuccess] = useState('');
  const isAdmin = esAdmin();
  
  const load = () => { setLoading(true); api.get('/registros/devoluciones').then(r => setRegistros(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const mostrarSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><RotateCcw size={24} color="var(--color-primary)" />Devoluciones de Producto Sanitario</h1>
          <p>{registros.length} devoluciones · Cada devolución se firma individualmente por la Directora Técnica</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> Nueva Devolución</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          registros.length === 0 ? <div className="empty-state"><RotateCcw size={48} /><h3>Sin devoluciones</h3></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Nº</th><th>Fecha</th><th>Artículo</th><th>Cant.</th><th>Lote</th><th>Motivo</th><th>Estado</th><th><Paperclip size={13} /></th></tr></thead>
                <tbody>{registros.map(r => 
                  <FilaDevolucion key={r.id} r={r} isAdmin={isAdmin}
                    onPasarPdte={setPasarPdteRegistro}
                    onRevertir={setRevertirRegistro}
                    onEditar={setEditarRegistro}
                    onEliminar={setEliminarRegistro} />)}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && <ModalDevolucion onClose={() => setModal(false)} onGuardado={() => { setModal(false); load(); }} />}
      
      {pasarPdteRegistro && <ModalPasarPdteFirma 
        endpoint={`/registros/devoluciones/${pasarPdteRegistro.id}/pasar-pdte-firma`}
        descripcion={`${pasarPdteRegistro.numero_devolucion} · ${pasarPdteRegistro.articulo}`}
        onClose={() => setPasarPdteRegistro(null)}
        onConfirmado={() => { setPasarPdteRegistro(null); load(); mostrarSuccess('Marcado como pendiente de firma. Ve a Firmas Pendientes.'); }} />}
      
      {revertirRegistro && <ModalRevertirBorrador 
        endpoint={`/registros/devoluciones/${revertirRegistro.id}/reset-estado`}
        descripcion={`${revertirRegistro.numero_devolucion} · ${revertirRegistro.articulo}`}
        onClose={() => setRevertirRegistro(null)}
        onConfirmado={() => { setRevertirRegistro(null); load(); mostrarSuccess('Revertido a borrador'); }} />}
      
      {editarRegistro && <ModalEditarDevolucion registro={editarRegistro} 
        onClose={() => setEditarRegistro(null)}
        onActualizado={() => { setEditarRegistro(null); load(); mostrarSuccess('Devolución actualizada'); }} />}
      
      {eliminarRegistro && <ModalEliminarRegistro 
        endpoint={`/registros/devoluciones/${eliminarRegistro.id}`}
        descripcion={`${eliminarRegistro.numero_devolucion} · ${eliminarRegistro.articulo}`}
        onClose={() => setEliminarRegistro(null)}
        onEliminado={() => { setEliminarRegistro(null); load(); mostrarSuccess('Devolución eliminada'); }} />}
    </div>
  );
}
